import { useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { logIn } from '../apollo/helpers/auth';
import EXCHANGE_AUTH_CODE from '../graphql/mutations/exchange-auth-code';
import useSnackBars from './useSnackBars';

/**
 * Hook to handle OAuth callbacks from backend authentication.
 *
 * When the backend completes OAuth authentication (e.g., Entra ID),
 * it redirects with a one-time code in the URL fragment:
 * - Success: /#auth_callback?code=...&auth_provider=...
 * - Error: /#auth_error?error=...&error_description=...
 *
 * The code must be exchanged for JWT tokens via GraphQL mutation.
 *
 * @param {string} redirectTo - Path to redirect after successful auth (default: '/app/home')
 */
const useOAuthCallback = (redirectTo = '/app/home') => {
  const history = useHistory();
  const { addAlert } = useSnackBars();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const reportError = (nextError) => {
    setError(nextError);
    addAlert({
      id: 'oauth-error',
      message: nextError.description,
      severity: 'error',
    });
  };

  const [exchangeAuthCode] = useMutation(EXCHANGE_AUTH_CODE, {
    onCompleted: ({ exchangeAuthCode: authData }) => {
      logIn(authData);
      history.replace(redirectTo);
      setProcessing(false);
    },
    onError: (mutationError) => {
      reportError({
        code: 'exchange_failed',
        description:
          mutationError.message || 'Código de autorización inválido o expirado',
      });
      setProcessing(false);
    },
  });

  useEffect(() => {
    const { hash } = window.location;

    if (!hash) return;

    if (hash.startsWith('#auth_callback?')) {
      setProcessing(true);
      window.history.replaceState(null, '', window.location.pathname);

      const params = new URLSearchParams(hash.replace('#auth_callback?', ''));
      const code = params.get('code');

      if (code) {
        exchangeAuthCode({ variables: { code } });
      } else {
        reportError({
          code: 'missing_code',
          description: 'No se recibió código de autorización',
        });
        setProcessing(false);
      }
      return;
    }

    if (hash.startsWith('#auth_error?')) {
      setProcessing(true);
      window.history.replaceState(null, '', window.location.pathname);

      const params = new URLSearchParams(hash.replace('#auth_error?', ''));
      reportError({
        code: params.get('error') || 'auth_error',
        description:
          params.get('error_description') || 'Error durante la autenticación',
      });
      setProcessing(false);
    }
  }, [history, addAlert, redirectTo, exchangeAuthCode]);

  return { processing, error };
};

export default useOAuthCallback;

